import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion } from "framer-motion";
import SiteNavbar from "../components/SiteNavbar";

// Student-specific hero imagery sourced from Pexels (licence-compliant)
import heroImgLight from "../assets/images/hero-students-light-pexels.jpeg";
import heroImgDark from "../assets/images/hero-students-dark-pexels.jpeg";
import dashboardImg from "../assets/images/hero-dashboard.jpeg";

/* ------------------------------------------------------------
 * Form schema & types
 * ----------------------------------------------------------*/

const referralSchema = z.object({
  teacherEmail: z.string().email("Enter a valid email address."),
  studentEmail: z.string().email("Enter a valid email address.").optional(),
});

type ReferralFields = z.infer<typeof referralSchema>;

export default function StudentLandingPage() {
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ReferralFields>({
    resolver: zodResolver(referralSchema),
  });

  const onSubmit = async (data: ReferralFields) => {
    // In a real implementation we would POST this to an API route or Supabase Function.
    // For now we just simulate a short network delay so the UI feels responsive.
    await new Promise((r) => setTimeout(r, 800));
    console.info("Student referral submitted", data);
    setSubmissionSuccess(true);
    reset();
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteNavbar />

      {/* ---------------------------------- */}
      {/* Hero                             */}
      {/* ---------------------------------- */}

      <section className="relative flex-1">
        {/* Background images – swap based on colour scheme */}
        <img
          src={heroImgLight}
          alt="Students collaborating in bright classroom"
          className="theme-light-only absolute inset-0 h-full w-full object-cover"
        />
        <img
          src={heroImgDark}
          alt="Students working on laptops in moody evening light"
          className="theme-dark-only absolute inset-0 h-full w-full object-cover"
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-gray-900/70 dark:via-gray-900/40 dark:to-gray-900" />

        <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center justify-center gap-8 px-6 py-36 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50 md:text-5xl"
          >
            Better feedback. Clearer progress. Fewer surprises.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="max-w-xl text-lg text-gray-700 dark:text-gray-300 md:text-2xl"
          >
            Help your educators discover AI-powered tools that keep you on track and make learning less stressful.
          </motion.p>

          {/* CTA form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="w-full max-w-md"
          >
            {submissionSuccess ? (
              <div className="rounded-md bg-green-50 p-6 text-center dark:bg-green-900/20">
                <p className="text-lg font-medium text-green-700 dark:text-green-300">
                  Thanks! We'll reach out to invite your educator.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                {/* Teacher email */}
                <div>
                  <label htmlFor="teacherEmail" className="sr-only">
                    Educator email
                  </label>
                  <input
                    id="teacherEmail"
                    type="email"
                    placeholder="Educator email address"
                    className={[
                      "block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
                      errors.teacherEmail ? "border-red-500" : "border-gray-300",
                    ].join(" ")}
                    {...register("teacherEmail")}
                  />
                  {errors.teacherEmail && (
                    <p className="mt-1 text-xs text-red-600">{errors.teacherEmail.message}</p>
                  )}
                </div>

                {/* Optional student email */}
                <div>
                  <label htmlFor="studentEmail" className="sr-only">
                    Your email (optional)
                  </label>
                  <input
                    id="studentEmail"
                    type="email"
                    placeholder="Your email (optional)"
                    className={[
                      "block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
                      errors.studentEmail ? "border-red-500" : "border-gray-300",
                    ].join(" ")}
                    {...register("studentEmail")}
                  />
                  {errors.studentEmail && (
                    <p className="mt-1 text-xs text-red-600">{errors.studentEmail.message}</p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white shadow hover:bg-primary-dark focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? "Sending…" : "Invite my educator"}
                </button>
              </form>
            )}
          </motion.div>

          {/* Photo credits */}
          <p className="mt-8 text-xs text-gray-500 dark:hidden">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@max-fischer"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Max Fischer
            </a>{' '}
            on{' '}
            <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
              Pexels
            </a>
          </p>
          <p className="mt-8 hidden text-xs text-gray-400 dark:block">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@tima-miroshnichenko"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Tima Miroshnichenko
            </a>{' '}
            on{' '}
            <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* ---------------------------------- */}
      {/* Feature Highlights                */}
      {/* ---------------------------------- */}
      <section className="mx-auto w-full max-w-7xl px-6 py-24 dark:text-gray-50">
        <h2 className="mb-16 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 md:text-4xl">
          Why you'll love ItsAcademic
        </h2>

        <div className="grid gap-16 md:grid-cols-2 md:gap-24">
          {/* Feature 1 */}
          <div className="flex flex-col justify-center gap-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Crystal-clear progress dashboards</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Know exactly where you stand in every course. See upcoming tasks, recent feedback and grades all in one place — no more hunting through multiple portals.
            </p>
          </div>
          <img
            src={dashboardImg}
            alt="Screenshot of student progress dashboard"
            className="w-full rounded-lg shadow-lg"
          />

          {/* Feature 2 */}
          {/* Light-mode illustration */}
          <img
            src={heroImgLight}
            alt="Students collaborating with educator"
            className="order-last w-full rounded-lg shadow-lg theme-light-only md:order-none"
          />
          {/* Dark-mode illustration */}
          <img
            src={heroImgDark}
            alt="Students collaborating with educator (dark mode)"
            className="order-last w-full rounded-lg shadow-lg theme-dark-only md:order-none"
          />
          <div className="flex flex-col justify-center gap-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Faster, richer feedback</h3>
            <p className="text-gray-700 dark:text-gray-300">
              AI helps your educators turn around assignments in hours, not weeks, with comments that actually make sense. More feedback, sooner, means better results.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------------------- */}
      {/* Callout                          */}
      {/* ---------------------------------- */}
      <section className="bg-primary py-20 text-center text-white">
        <h2 className="mb-6 text-3xl font-bold tracking-tight md:text-4xl">
          Want ItsAcademic at your school?
        </h2>
        <p className="mx-auto max-w-2xl text-lg opacity-90">
          Share your educator’s email and we'll send them an invitation with everything they need to get started.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t bg-gray-50 py-6 text-center text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
        © {new Date().getFullYear()} ItsAcademic.ai
      </footer>
    </div>
  );
}
